import type { LucideIcon } from "lucide-react";
import { cn } from "@/lib/cn";

/**
 * Tone decides what the card MEANS, never which dealer is looking at it.
 *
 * - `danger` / `warning` are fixed SEMANTIC tokens (DEV_RULES §10.10): a
 *   problem looks like a problem in every palette, and a dealer must not be
 *   able to rebrand "late" into their house colour.
 * - `brand` is the runtime dealer token — used for the one card that is
 *   ordinary pending work rather than a fault («Черновики»: routes built and
 *   waiting to be sent, which is simply the next step of the morning).
 *
 * The mockup's orange/red/blue are ONE dealer's palette as rendered; what is
 * copied here is each colour's ROLE, never its value.
 */
export type ActionCardTone = "danger" | "warning" | "brand";

interface Props {
  icon: LucideIcon;
  /** Card heading — «Не распределено». */
  title: string;
  /** The measured number. Never a placeholder: a card exists only when measured. */
  count: number;
  /** Unit beside the number — «заказа» / «маршрута» / «машина». */
  unit: string;
  /** One line of context, or null when the source gave nothing to say. */
  detail: string | null;
  tone: ActionCardTone;
  /** Label of the action that starts the fix. */
  actionLabel: string;
  onAction: () => void;
  /** Disables the action and explains why, when the destination cannot be reached. */
  actionDisabledReason?: string;
}

/**
 * Tint + icon + accent for each tone.
 *
 * `/10` and `/30` alpha steps on the token itself, so a dealer's palette (or a
 * future token change) carries through instead of a frozen pastel. No literal
 * colour appears anywhere in this map.
 */
const TONE_STYLES: Record<
  ActionCardTone,
  { surface: string; icon: string; value: string; button: string }
> = {
  danger: {
    surface: "border-brand-danger/30 bg-brand-danger/5",
    icon: "bg-brand-danger/10 text-brand-danger",
    value: "text-brand-danger",
    button:
      "bg-brand-primary text-white hover:bg-brand-primary-hover focus-visible:ring-brand-primary",
  },
  warning: {
    surface: "border-brand-warning/40 bg-brand-warning/5",
    icon: "bg-brand-warning/15 text-brand-warning",
    value: "text-gray-900",
    button:
      "border border-brand-warning/50 bg-white text-gray-900 hover:bg-brand-warning/10 focus-visible:ring-brand-warning",
  },
  brand: {
    surface: "border-brand-primary/30 bg-brand-primary/5",
    icon: "bg-brand-primary/10 text-brand-primary",
    value: "text-gray-900",
    button:
      "bg-brand-primary text-white hover:bg-brand-primary-hover focus-visible:ring-brand-primary",
  },
};

/**
 * One «ТРЕБУЕТ РЕШЕНИЯ» card: a number, what it means right now, and the button
 * that starts fixing it.
 *
 * This component never decides whether to render — the band does, by simply not
 * building a card whose count is zero or unmeasured (`buildActionCards`). There
 * is deliberately no "empty" or "all clear" state here: a card that can render
 * at zero re-creates the scoreboard this redesign replaces.
 */
export function ActionCard({
  icon: Icon,
  title,
  count,
  unit,
  detail,
  tone,
  actionLabel,
  onAction,
  actionDisabledReason,
}: Props) {
  const styles = TONE_STYLES[tone];

  return (
    <div
      className={cn(
        "flex flex-col gap-3 rounded-2xl border p-4 shadow-sm",
        styles.surface,
      )}
    >
      <div className="flex items-center gap-2">
        <span
          className={cn(
            "flex h-7 w-7 shrink-0 items-center justify-center rounded-lg",
            styles.icon,
          )}
        >
          <Icon className="h-4 w-4" aria-hidden="true" />
        </span>
        <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
      </div>

      <p className="flex items-baseline gap-2">
        <span
          className={cn(
            "text-3xl font-bold leading-none tracking-tight",
            styles.value,
          )}
        >
          {count}
        </span>
        <span className="text-sm text-gray-500">{unit}</span>
      </p>

      {/* min-h keeps the four cards' buttons on one line even when a card has
          nothing to say — without reserving a phantom row of text. */}
      <p className="min-h-[1.25rem] text-xs leading-5 text-gray-500">
        {detail}
      </p>

      <button
        type="button"
        onClick={onAction}
        disabled={actionDisabledReason != null}
        title={actionDisabledReason}
        className={cn(
          "inline-flex h-9 cursor-pointer items-center justify-center self-start rounded-lg px-3.5 text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
          styles.button,
        )}
      >
        {actionLabel}
      </button>
    </div>
  );
}
